import { useToast } from '@app/core/notifications';
import { routeGenerator } from '@app/modules/fragments/routing/FragmentsRoutes';
import { Station, useRouter } from '@mi-front/core';
import DeleteIcon from '@mui/icons-material/Delete';
import { LoadingButton } from '@mui/lab';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from '@mui/material';
import { ReactNode, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { apiClient } from '../../../../api';
import { Fragment } from '../../types';

/**
 * Delete fragment view: trigger button and MUI confirmation dialog.
 * On confirm, deletes the fragment and redirects to the list.
 */
export const FragmentsDeleteView = (p: { station: Station; fragment: Fragment }) => {
  const { history } = useRouter();
  const { $t } = useIntl();
  const { notifySuccess, notifyError } = useToast();
  const [open, setOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      await apiClient.fragments.delete(p.fragment.id);
      notifySuccess(
        $t({
          id: 'fragments.deleteScreen.deleted',
          defaultMessage: 'Fragmento eliminado',
        }),
      );
      setOpen(false);
      history.replace(routeGenerator.list(p.station.slug));
    } catch (err: unknown) {
      notifyError(
        $t({
          id: 'fragments.deleteScreen.deleteError',
          defaultMessage: 'No se pudo eliminar el fragmento',
        }),
      );
      throw err;
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <>
      <Button
        size="small"
        variant="outlined"
        color="error"
        onClick={() => setOpen(true)}
        startIcon={<DeleteIcon />}
      >
        <FormattedMessage id="common.actions.delete" defaultMessage="Eliminar" />
      </Button>

      <Dialog maxWidth="sm" fullWidth open={open} onClose={() => !isDeleting && setOpen(false)}>
        <DialogTitle>
          <FormattedMessage id="fragments.deleteScreen.title" defaultMessage="Eliminar fragmento" />
        </DialogTitle>
        <DialogContent>
          <DialogContentText component="div">
            <FormattedMessage
              id="fragments.deleteScreen.prompt"
              defaultMessage={`
            ¿Deseas eliminar el fragmento <b>{title}</b>? {br}
            Esta acción no se puede deshacer.
          `}
              values={{
                title: p.fragment.title,
                br: <br />,
                b: (chunks: ReactNode) => <b>{chunks}</b>,
              }}
            />
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)} disabled={isDeleting}>
            <FormattedMessage id="common.actions.cancel" defaultMessage="Cancelar" />
          </Button>
          <LoadingButton
            onClick={handleDelete}
            loading={isDeleting}
            color="error"
            variant="contained"
          >
            <FormattedMessage id="common.actions.delete" defaultMessage="Eliminar" />
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </>
  );
};
